import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError, useAuth, API } from "@/lib/auth";
import NexWordmark from "@/components/NexWordmark";
import { KindIcon, KindBadge, StatusBadge, fmtBytes, KIND_META, STATUS_META } from "./kindMeta";
import {
  ArrowLeft, Upload, Search, Loader2, Trash2, FileText, ExternalLink, Pencil,
  Calendar, X, ChevronDown, Check,
} from "lucide-react";
import { toast } from "sonner";

// Accept full multi-format catalogue including images (per owner request)
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif";

const DocumentCenterPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadQueue, setUploadQueue] = useState([]);   // [{name, status: 'pending'|'uploading'|'done'|'error', error?}]
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [folder, setFolder] = useState("");
  const [folders, setFolders] = useState([]);
  const loadFolders = useCallback(() => apiClient.get("/documents/folders").then(({ data }) => setFolders(data.folders || [])).catch(() => {}), []);
  useEffect(() => { loadFolders(); }, [loadFolders]);
  const moveToFolder = async (id, name) => {
    try { await apiClient.patch(`/documents/${id}`, { folder: name }); toast.success(name ? `Moved to ${name}` : "Removed from folder"); load(); loadFolders(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Rename modal (partner)
  const [renaming, setRenaming] = useState(null);
  const [r_filename, setRFilename] = useState("");
  const [r_note, setRNote] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  // Upload form
  const [contactId, setContactId] = useState("");
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  // Inline status editor state
  const [statusOpenId, setStatusOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (kind) params.set("kind", kind);
      if (folder) params.set("folder", folder);
      if (search.trim()) params.set("search", search.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (statusFilter) params.set("invoice_status", statusFilter);
      const { data } = await apiClient.get(`/documents?${params.toString()}`);
      setItems(data.items);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [kind, folder, search, dateFrom, dateTo, statusFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/crm/contacts?limit=500");
        setContacts(data.items);
      } catch { /* ignore */ }
    })();
  }, []);

  // Multi-file upload — process sequentially so progress is legible + storage
  // key rotation logic in the backend stays predictable.
  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    setUploadQueue(list.map(f => ({ name: f.name, status: "pending" })));
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      setUploadQueue(q => q.map((it, ix) => ix === i ? { ...it, status: "uploading" } : it));
      try {
        const fd = new FormData();
        fd.append("file", f);
        if (contactId) fd.append("contact_id", contactId);
        if (note.trim()) fd.append("note", note.trim());
        await apiClient.post("/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUploadQueue(q => q.map((it, ix) => ix === i ? { ...it, status: "done" } : it));
      } catch (err) {
        setUploadQueue(q => q.map((it, ix) => ix === i ? { ...it, status: "error", error: formatApiError(err) } : it));
      }
    }
    const done = uploadQueue.filter(x => x.status === "done").length;
    toast.success(`Uploaded ${list.length} file${list.length === 1 ? "" : "s"}${done ? "" : ""}`);
    setNote("");
    if (fileRef.current) fileRef.current.value = "";
    // Clear the queue banner after a short delay so operators can read it
    setTimeout(() => setUploadQueue([]), 3500);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await apiClient.delete(`/documents/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setStatus = async (id, status) => {
    setStatusOpenId(null);
    try {
      await apiClient.patch(`/documents/${id}/status`, { status });
      toast.success(status === "none" ? "Status cleared" : `Marked ${STATUS_META[status].label}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openRename = (d) => {
    setRenaming(d);
    setRFilename(d.original_filename || "");
    setRNote(d.note || "");
  };

  const saveRename = async (e) => {
    e.preventDefault();
    if (!renaming) return;
    setRenameSaving(true);
    try {
      await apiClient.patch(`/documents/${renaming.id}`, {
        original_filename: r_filename,
        note: r_note,
      });
      toast.success("Document updated");
      setRenaming(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRenameSaving(false); }
  };

  const openDoc = (id) => {
    const token = localStorage.getItem("nex_token");
    window.open(`${API}/documents/${id}/download?auth=${encodeURIComponent(token)}`, "_blank", "noopener");
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const files = e.dataTransfer.files;
    if (files?.length) uploadFiles(files);
  };

  const busy = uploadQueue.some(x => x.status === "uploading" || x.status === "pending");
  const clearDateFilter = () => { setDateFrom(""); setDateTo(""); };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <div className="border-b border-nex-border bg-black/40 backdrop-blur">
        <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-4 flex items-center gap-3">
          <Link to="/admin" className="flex items-center gap-2 text-nex-chrome hover:text-nex-gold">
            <ArrowLeft size={14} /> <NexWordmark size="text-xl" />
          </Link>
          <span className="mono-body text-[10px] tracking-[0.25em] uppercase text-nex-gold">// DOC CENTER</span>
        </div>
      </div>

      <div className="w-full mx-auto px-6 md:px-10 lg:px-16 xl:px-24 2xl:px-32 py-8">
        <div className="mb-6">
          <div className="mono-label mb-2">// FILE VAULT · INVOICE TRACKER</div>
          <h1 className="serif-head text-3xl">Doc Center</h1>
          <p className="text-nex-chrome mt-1 text-sm">
            PDFs · Word · Excel · CSVs · Images — persistent object storage · attach to any Contact · tag invoices by payment status
          </p>
        </div>

        {/* UPLOAD */}
        <div
          className={`card-industrial corner-tick p-6 mb-6 transition ${dragOver ? "border-nex-gold bg-nex-gold/5" : ""}`}
          data-testid="upload-block"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <div className="flex items-center gap-2 mb-4">
            <Upload size={16} className="text-nex-gold" />
            <div className="mono-label">// UPLOAD · MULTI-FILE</div>
          </div>
          <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 items-start">
            <select value={contactId} onChange={e => setContactId(e.target.value)} className="input-industrial" data-testid="upload-contact">
              <option value="">— No contact / general —</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.full_name}{c.business_name ? ` · ${c.business_name}` : ""}</option>)}
            </select>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note applied to every file (optional)" className="input-industrial" data-testid="upload-note" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="tick-btn-primary text-xs !py-2 !px-4 justify-center"
              data-testid="pick-file-btn"
            >
              {busy ? <><Loader2 size={13} className="animate-spin" /> Uploading</> : <><Upload size={13} /> Pick files</>}
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept={ACCEPT}
              onChange={e => uploadFiles(e.target.files)}
              className="hidden"
              data-testid="file-input"
            />
          </div>
          <div className="mono-body text-[10px] text-nex-muted mt-3 text-center">
            or drag & drop multiple files · max 25 MB each · PDF · Word · Excel · CSV · Text · JPG/PNG/WebP
          </div>

          {/* Multi-upload progress */}
          {uploadQueue.length > 0 && (
            <div className="mt-4 border border-nex-border/50 divide-y divide-nex-border/40" data-testid="upload-queue">
              {uploadQueue.map((f, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2">
                  <span className="text-nex-silver text-xs truncate mr-3">{f.name}</span>
                  <span className="flex items-center gap-1 mono-body text-[10px] uppercase tracking-widest shrink-0">
                    {f.status === "uploading" && <><Loader2 size={11} className="animate-spin text-nex-gold" /> <span className="text-nex-gold">Uploading</span></>}
                    {f.status === "pending" && <span className="text-nex-muted">Queued</span>}
                    {f.status === "done" && <><Check size={11} className="text-emerald-300" /> <span className="text-emerald-300">Done</span></>}
                    {f.status === "error" && <><X size={11} className="text-red-300" /> <span className="text-red-300" title={f.error}>Failed</span></>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* FILTERS */}
        <div className="grid md:grid-cols-[1fr_170px_170px_170px_170px] gap-3 mb-3" data-testid="doc-filters">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search filename or note" className="input-industrial !pl-9" data-testid="doc-search" />
          </div>
          <select value={folder} onChange={e => setFolder(e.target.value)} className="input-industrial" data-testid="folder-filter">
            <option value="">All folders</option>
            {folders.map(f => <option key={f.name} value={f.name}>{`${f.name} (${f.count})`}</option>)}
          </select>
          <select value={kind} onChange={e => setKind(e.target.value)} className="input-industrial" data-testid="kind-filter">
            <option value="">All file types</option>
            {Object.entries(KIND_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="input-industrial" data-testid="status-filter">
            <option value="">All statuses</option>
            <option value="none">No status</option>
            <option value="pending">Pending Payment</option>
            <option value="paid">Paid</option>
            <option value="disputed">Disputed</option>
          </select>
          <div className="flex items-center gap-2">
            {(dateFrom || dateTo) && (
              <button onClick={clearDateFilter} className="text-nex-muted hover:text-red-300 p-2" title="Clear dates" data-testid="clear-date-filter">
                <X size={14} />
              </button>
            )}
            <div className="mono-body text-[10px] uppercase tracking-widest text-nex-muted shrink-0">Dates</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div className="relative">
            <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted pointer-events-none" />
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="input-industrial !pl-9"
              data-testid="date-from"
              aria-label="Filter from date"
            />
          </div>
          <div className="relative">
            <Calendar size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-nex-muted pointer-events-none" />
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="input-industrial !pl-9"
              data-testid="date-to"
              aria-label="Filter to date"
            />
          </div>
        </div>

        {/* LIST */}
        <div className="card-industrial corner-tick overflow-visible" data-testid="doc-list">
          {loading ? (
            <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-nex-chrome text-sm">
              <FileText className="text-nex-muted mx-auto mb-3" size={28} />
              No documents match your filters. Drop files above or clear filters.
            </div>
          ) : (
            <div className="divide-y divide-nex-border/40">
              {items.map(d => (
                <div key={d.id} className="flex items-center gap-4 p-4 hover:bg-white/[0.02] relative" data-testid={`doc-row-${d.id}`}>
                  <KindIcon kind={d.kind} />
                  <div className="flex-1 min-w-0">
                    <div className="text-nex-silver truncate">{d.original_filename}</div>
                    <div className="mono-body text-[10px] text-nex-muted mt-1 flex flex-wrap gap-2">
                      <span>{fmtBytes(d.size)}</span>
                      <span>·</span>
                      <span>{new Date(d.created_at).toLocaleDateString()}</span>
                      <span>·</span>
                      <span>{d.uploaded_by}</span>
                      <span>·</span>
                      <select value={d.folder || ""} onChange={e => moveToFolder(d.id, e.target.value)} data-testid={`doc-folder-${d.id}`}
                        className="bg-transparent border border-nex-border/60 text-[10px] text-nex-chrome px-1 py-0.5 hover:border-nex-gold/50">
                        <option value="">No folder</option>
                        {folders.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                      </select>
                      {d.contact && (
                        <>
                          <span>·</span>
                          <Link to={`/admin/contacts/${d.contact.id}`} className="text-nex-gold hover:text-nex-silver">
                            {d.contact.full_name}{d.contact.business_name ? ` · ${d.contact.business_name}` : ""}
                          </Link>
                        </>
                      )}
                    </div>
                    {d.note && <div className="text-nex-chrome text-xs mt-1">{d.note}</div>}
                  </div>
                  <KindBadge kind={d.kind} />

                  {/* Status pill + dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setStatusOpenId(statusOpenId === d.id ? null : d.id)}
                      data-testid={`status-btn-${d.id}`}
                      className="flex items-center gap-1"
                      title="Change payment status"
                    >
                      {d.invoice_status && d.invoice_status !== "none"
                        ? <StatusBadge status={d.invoice_status} />
                        : <span className="mono-body text-[10px] tracking-[0.15em] uppercase px-2 py-1 border border-dashed border-nex-border text-nex-muted hover:border-nex-gold hover:text-nex-gold">Tag status</span>}
                      <ChevronDown size={10} className="text-nex-muted" />
                    </button>
                    {statusOpenId === d.id && (
                      <div
                        className="absolute right-0 top-full mt-1 z-30 card-industrial border border-nex-border/70 shadow-xl min-w-[180px]"
                        data-testid={`status-menu-${d.id}`}
                        onMouseLeave={() => setStatusOpenId(null)}
                      >
                        {["pending", "paid", "disputed", "none"].map(s => (
                          <button
                            key={s}
                            onClick={() => setStatus(d.id, s)}
                            data-testid={`status-opt-${d.id}-${s}`}
                            className={`w-full text-left px-3 py-2 text-xs border-b border-nex-border/30 hover:bg-nex-gold/10 flex items-center gap-2 ${STATUS_META[s].color}`}
                          >
                            <span className={`w-2 h-2 rounded-full ${STATUS_META[s].bg.split(" ")[0]}`} />
                            {STATUS_META[s].label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    <button onClick={() => openDoc(d.id)} title="Open" className="tick-btn-secondary text-xs !py-1.5 !px-2" data-testid={`open-doc-${d.id}`}>
                      <ExternalLink size={13} />
                    </button>
                    {user?.is_partner && (
                      <button onClick={() => openRename(d)} title="Rename / edit note" className="text-nex-muted hover:text-nex-gold p-2" data-testid={`edit-doc-${d.id}`}>
                        <Pencil size={14} />
                      </button>
                    )}
                    <button onClick={() => remove(d.id)} title="Delete" className="text-nex-muted hover:text-red-300 p-2" data-testid={`delete-doc-${d.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {renaming && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={() => setRenaming(null)} />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-full max-w-lg px-4" data-testid="rename-doc-modal">
            <div className="bg-nex-panel border border-nex-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="serif-head text-xl text-nex-silver">Rename / Edit Note</h3>
                <button onClick={() => setRenaming(null)} className="text-nex-muted hover:text-nex-silver" data-testid="rename-doc-close"><X size={18} /></button>
              </div>
              <form onSubmit={saveRename} className="space-y-3">
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Filename</div>
                  <input type="text" value={r_filename} onChange={(e) => setRFilename(e.target.value)} className="input-industrial w-full" data-testid="rename-doc-filename" />
                </label>
                <label className="block">
                  <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Note</div>
                  <textarea rows={3} value={r_note} onChange={(e) => setRNote(e.target.value)} className="input-industrial w-full" data-testid="rename-doc-note" />
                </label>
                <button type="submit" disabled={renameSaving} className="tick-btn-primary w-full justify-center" data-testid="rename-doc-save">
                  {renameSaving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentCenterPage;
